import { hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type {
	DSSetup,
	ValidationFunction as MetricValidationFunction,
	PopupData,
	Service,
} from 'webapp-types';

import CommonMetricAttributesUnitFieldsetComponent from 'webapp/components/common/metric-attributes-unit-fieldset';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class MetricsPopupCompareOtherMetricComponent extends PopupInnerModalComponent<PopupData.Metrics.CompareOtherMetric> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked metricSetupData: DSSetup.MetricSetupData | null = null;

	validateMetricFieldset: null | MetricValidationFunction = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const model = this.model;
		if (model) {
			this.metricSetupData = model.metricSetup.metricSetupData || {
				key: '',
				spaceAccessId: null,
			};
		}

		void this.mmMetricsService.prepareForMetricEditing();
	}

	get model() {
		return this.popupData.model;
	}

	@action
	bindMetricFieldsetValidation(validationFn: MetricValidationFunction) {
		this.validateMetricFieldset = validationFn;
	}

	@action
	changeMetricSetupData(newValue: DSSetup.MetricSetupData): void {
		this.metricSetupData = newValue;
	}

	@action
	save(): void {
		const model = this.model;
		if (!model) {
			return;
		}

		const errors = this.validateMetricFieldset?.() || {};

		if (Object.keys(errors).length !== 0) {
			return;
		}

		const newCompare = model.createCopy();

		const metricSetup = newCompare.metricSetup;
		metricSetup.setFromMetricSetupData(this.metricSetupData);

		this.popupData.onSave(newCompare);

		this.close();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Metrics" @size="lg" />
			Select metric
		</div>

		<div class="content">
			<CommonMetricAttributesUnitFieldsetComponent
				@model={{this.metricSetupData}}
				@formOptions={{hash legend="Select a metric to compare to" showErrorMessages=true}}
				@changeMetricSetupData={{this.changeMetricSetupData}}
				@validationBinder={{this.bindMetricFieldsetValidation}}
				@requireSingleOutput={{true}}
				@onContentChange={{this.scheduleReposition}}
			/>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@onTrigger={{this.save}}
					/>
				</div>
			</div>
		</div>
	</template>
}
